/**
 * The pieces shared by the two ADDIS status surfaces (A7Y-3808): the compact
 * one-row-per-system table on `/admin/automation`, and the full status block at
 * the top of each system's own page (`/admin/automation/cdid` and siblings).
 *
 * They live here for the same reason `quota-account-parts.tsx` exists — so the
 * overview row and the detail block cannot drift into rendering the same system
 * differently. The split the overhaul asked for is how MUCH of a system is
 * shown, never what any of it means.
 *
 * Every light, reason and attribution is still decided server-side by
 * `/a7y-addis-status`; nothing here infers a colour. Two of the command's rules
 * shape this layout and are load-bearing in both surfaces:
 *   - a source that could not be READ is not a source that returned nothing, so
 *     `runs: null` and `[]` render differently, and every null chain field reads
 *     "unknown" rather than "no";
 *   - a red light on a pipeline stage is not an accusation against that stage,
 *     so `attribution` travels with the light wherever the light goes.
 */
import type {
  AddisLight,
  AddisWorkflowGroup,
  AddisWorkflowHealth,
  AddisWorkflowRun,
} from '@a7y/types';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LIGHT_PRESENTATION,
  consoleRunListPath,
  runDetailTarget,
  workflowRunsUrl,
} from './addis-status-meta';
import { fmtDuration, fmtLocal, fmtUtc } from './shared';

/**
 * The light, as the glyph ALONE (TPF, 2026-09-09) — the dot already says which
 * colour it is, so printing "Green" beside a green dot was one word of pure
 * redundancy on every row.
 *
 * The word is not lost, it moves: it stays in the `aria-label` and the `title`,
 * because a screen reader is exactly the reader that cannot see the colour. The
 * reason travels with it in both, which is also why this is a local badge rather
 * than `platform-health/Stoplight` — that component welds its `reason` inline
 * after the label, and these surfaces need the reason on its own line with the
 * rule-2 `attribution` in the reason's place. Forking it outright would give
 * platform health a second colour scale to drift against.
 */
export function AddisLightBadge({ light, reason }: { light: AddisLight; reason: string }) {
  const { glyph, label } = LIGHT_PRESENTATION[light];
  return (
    <span
      role="status"
      aria-label={`${label} — ${reason}`}
      title={`${label} — ${reason}`}
      className="inline-flex items-center text-sm leading-none"
    >
      <span aria-hidden="true">{glyph}</span>
    </span>
  );
}

/** A horizontal rule with a label riding on it — the console's `═══ … ═══`. */
export function BannerRule({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="h-px w-6 shrink-0 bg-zinc-300 dark:bg-zinc-700" />
      <span className="font-mono text-[11px] whitespace-nowrap uppercase tracking-[0.18em] text-zinc-500">
        {children}
      </span>
      <span className="h-px flex-1 bg-zinc-300 dark:bg-zinc-700" />
    </div>
  );
}

const RUN_TICK_CLASSES: Record<string, string> = {
  success: 'bg-emerald-500',
  failure: 'bg-red-500',
  cancelled: 'bg-zinc-400 dark:bg-zinc-600',
  skipped: 'bg-zinc-400 dark:bg-zinc-600',
};

/** A run with no conclusion has not finished, which is amber and moving — never
 *  grey, which would read as "declined to act". */
function runTickClass(run: AddisWorkflowRun): string {
  return run.conclusion == null
    ? 'bg-amber-400 animate-pulse'
    : (RUN_TICK_CLASSES[run.conclusion] ?? 'bg-zinc-400 dark:bg-zinc-600');
}

/** The workflow is named even in the per-workflow strip, where the label sits
 *  beside it: in the merged system strip a bar could be from any of the
 *  system's files, and one title format is better than two. */
function runTickTitle(run: AddisWorkflowRun): string {
  const outcome = run.conclusion ?? run.status;
  const took = run.durationSeconds == null ? '' : ` · ${fmtDuration(run.durationSeconds)}`;
  return `${run.workflow} ${run.runId} — ${outcome} · ${run.event} · ${fmtLocal(run.startedAt)}${took}`;
}

/** One tick. A link, because a tick's whole job is to get you to its run. */
function RunTick({ run }: { run: AddisWorkflowRun }) {
  const title = runTickTitle(run);
  return (
    <a
      href={run.url}
      target="_blank"
      rel="noreferrer"
      title={title}
      aria-label={title}
      className={cn(
        'h-3.5 w-1.5 rounded-[1px] transition-transform hover:scale-y-125',
        runTickClass(run)
      )}
    />
  );
}

/**
 * One workflow file's recent run outcomes, newest first — the group tables'
 * Status cell. Same ticks as the merged system strip, one file's worth.
 *
 * A run list that could not be READ renders a dash, never an empty strip: "we
 * could not look" and "nothing ran" are different facts and the command's rule
 * 3 forbids sharing a rendering between them. A file that genuinely has no runs
 * says so — which is the honest reading for a `workflow_call`-only file such as
 * `aquix-runner.yml`, whose runs GitHub attributes to its caller.
 */
export function WorkflowRunStrip({ health }: { health: AddisWorkflowHealth }) {
  if (health.runs == null) {
    return (
      <span
        className="text-xs text-amber-700 dark:text-amber-400"
        title={health.error ?? 'Runs could not be read'}
      >
        —
      </span>
    );
  }
  if (health.runs.length === 0) {
    return (
      <span className="text-xs text-zinc-400" title="No runs in the window">
        no runs
      </span>
    );
  }
  return (
    <span
      className="flex items-center gap-0.5"
      data-testid={`addis-workflow-strip-${health.workflow}`}
    >
      {health.runs.map((run) => (
        <RunTick key={run.runId} run={run} />
      ))}
    </span>
  );
}

/** The newest run's outcome and when it started — the row's "what happened". */
function LastRunCell({ health }: { health: AddisWorkflowHealth }) {
  if (health.runs == null) {
    return (
      <span className="text-amber-700 dark:text-amber-400">
        {health.error ?? 'Runs could not be read.'}
      </span>
    );
  }
  const latest = health.latest;
  if (!latest) return <span className="text-zinc-400">never run in the window</span>;
  // A run with no conclusion has not finished; saying "success" for it would be
  // a guess, and saying nothing would hide that something is in flight.
  const outcome = latest.conclusion ?? `${latest.status}…`;
  const failed = latest.conclusion != null && latest.conclusion !== 'success';
  const took = latest.durationSeconds == null ? '' : ` · ${fmtDuration(latest.durationSeconds)}`;
  return (
    <span
      className={cn('tabular-nums', failed ? 'text-red-700 dark:text-red-400' : undefined)}
      title={fmtUtc(latest.startedAt)}
    >
      {fmtLocal(latest.startedAt)} — {outcome}
      {took}
    </span>
  );
}

function RecordCell({ health }: { health: AddisWorkflowHealth }) {
  if (health.runs == null) {
    // Not "0/0 green" — that would report a measurement nobody took.
    return <span className="text-amber-700 dark:text-amber-400">unreadable</span>;
  }
  if (!health.greenOfRecent) return <span className="text-zinc-400">—</span>;
  const { green, of } = health.greenOfRecent;
  return (
    <span
      className={cn(
        'tabular-nums',
        green === of ? 'text-zinc-600 dark:text-zinc-300' : 'text-amber-700 dark:text-amber-400'
      )}
    >
      {green}/{of} green
    </span>
  );
}

/** Shared look for the two row actions — quiet until the row is hovered. */
const ACTION_CLASS =
  'rounded px-1.5 py-0.5 text-[11px] text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100';

/**
 * The two links at the end of a row: this run, and every run of this workflow.
 *
 * Each falls back to GitHub independently and for a DIFFERENT reason — `detail`
 * because the run produced no flight-recorder session, `see all` because no
 * console page lists that workflow. A row can therefore carry one console link
 * and one GitHub link, which is accurate rather than inconsistent. `↗` marks
 * the ones that leave the console.
 *
 * `stopPropagation` on every link: the whole row is also a click target for
 * detail, and without it a click on "see all" would fire both.
 */
function RowActions({ health }: { health: AddisWorkflowHealth }) {
  const detail = runDetailTarget(health);
  const listPath = consoleRunListPath(health.workflow);
  const stop = (event: React.MouseEvent) => event.stopPropagation();
  return (
    <div className="flex items-center justify-end gap-1 whitespace-nowrap">
      {detail == null ? (
        <span
          className="px-1.5 text-[11px] text-zinc-300 dark:text-zinc-700"
          title="No run to open"
        >
          detail
        </span>
      ) : 'to' in detail ? (
        <Link to={detail.to} onClick={stop} className={ACTION_CLASS}>
          detail
        </Link>
      ) : (
        <a
          href={detail.href}
          target="_blank"
          rel="noreferrer"
          onClick={stop}
          title="This run on GitHub — it produced no console session"
          className={ACTION_CLASS}
        >
          detail ↗
        </a>
      )}
      {listPath ? (
        <Link to={listPath} onClick={stop} className={ACTION_CLASS}>
          see all
        </Link>
      ) : (
        <a
          href={workflowRunsUrl(health.workflow)}
          target="_blank"
          rel="noreferrer"
          onClick={stop}
          title="Every run of this workflow on GitHub — the console has no page for it"
          className={ACTION_CLASS}
        >
          see all ↗
        </a>
      )}
    </div>
  );
}

function WorkflowRow({ health }: { health: AddisWorkflowHealth }) {
  const navigate = useNavigate();
  const detail = runDetailTarget(health);
  // The row opens whatever `detail` points at; an external target opens in a
  // new tab, exactly as the link itself would.
  const openDetail = () => {
    if (!detail) return;
    if ('to' in detail) navigate(detail.to);
    else window.open(detail.href, '_blank', 'noopener,noreferrer');
  };
  return (
    <tr
      onClick={openDetail}
      className={cn(
        'align-middle hover:bg-zinc-50 dark:hover:bg-zinc-900',
        detail && 'cursor-pointer'
      )}
      data-testid={`addis-workflow-row-${health.workflow}`}
    >
      <td className="w-px px-3 py-2">
        <WorkflowRunStrip health={health} />
      </td>
      {/* The file name is the row's identity, and it points at GitHub's page
          for that workflow's runs — the ticks already link to single runs, so
          this is the only remaining hop a reader needs. */}
      <td className="px-3 py-2">
        <a
          href={workflowRunsUrl(health.workflow)}
          target="_blank"
          rel="noreferrer"
          className="font-mono text-xs whitespace-nowrap text-zinc-800 hover:underline dark:text-zinc-200"
        >
          {health.workflow}
        </a>
      </td>
      <td className="px-3 py-2 text-xs text-zinc-600 dark:text-zinc-400">
        <LastRunCell health={health} />
      </td>
      <td className="w-px whitespace-nowrap px-3 py-2 text-xs">
        <RecordCell health={health} />
      </td>
      <td className="w-px px-3 py-2">
        <RowActions health={health} />
      </td>
    </tr>
  );
}

/**
 * A bare table of workflow rows — no heading, no light, no description.
 *
 * Extracted from `GroupTable` so the APHID Status page can render one per
 * subsystem (PULSE, RAVEN, AXON, …) without inventing a light for each: a
 * subsystem is a slice of one group, and the group's light already covers the
 * whole of it. `GroupTable` is now this plus that heading.
 */
/**
 * The group's rows, bucketed into the server's sections — one small table per
 * section, under its own heading.
 *
 * The order is the server's and is never re-derived here: `AddisWorkflowGroup`
 * emits its workflows section-contiguously, so first appearance IS declaration
 * order and the console needs no list of section names to keep in step (the
 * APHID page used to carry one). A row the server did not classify still gets a
 * table — `Unassigned` — because an older server is a reason to render a plain
 * list, never a reason to drop a workflow.
 *
 * Every section is headed, including a lone `Unassigned` — a table with no
 * heading would hide the fact that the rows arrived unclassified, and that is
 * the one thing the fallback exists to say.
 */
export function WorkflowSections({
  workflows,
  testIdPrefix = 'addis-section',
}: {
  workflows: readonly AddisWorkflowHealth[];
  testIdPrefix?: string;
}) {
  return (
    <div className="space-y-3">
      {sectionsOf(workflows).map(([label, rows]) => (
        <section key={label} className="space-y-1.5" data-testid={`${testIdPrefix}-${label}`}>
          <h4 className="font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-500">
            {label}
          </h4>
          <WorkflowTable workflows={rows} />
        </section>
      ))}
    </div>
  );
}

/**
 * Buckets rows by section, preserving the order the server sent them in.
 *
 * Deliberately NOT exported: every other export in this file is a component,
 * and `react-refresh/only-export-components` warns on a mixed module — which
 * `eslint --max-warnings 0` turns into a red `CI Gate`. `WorkflowSections` is
 * the shared surface both pages consume, so nothing outside needs the helper.
 */
function sectionsOf(
  workflows: readonly AddisWorkflowHealth[]
): Array<[string, AddisWorkflowHealth[]]> {
  const bySection = new Map<string, AddisWorkflowHealth[]>();
  for (const health of workflows) {
    const key = health.section || 'Unassigned';
    const bucket = bySection.get(key);
    if (bucket) bucket.push(health);
    else bySection.set(key, [health]);
  }
  return [...bySection.entries()];
}

export function WorkflowTable({ workflows }: { workflows: readonly AddisWorkflowHealth[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
      <table className="min-w-full text-sm">
        <thead className="bg-zinc-50 text-left text-xs text-zinc-500 dark:bg-zinc-900">
          <tr>
            <th className="w-px px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Workflow</th>
            <th className="px-3 py-2 font-medium">Last run</th>
            <th className="w-px px-3 py-2 font-medium">Record</th>
            <th className="w-px px-3 py-2 font-medium">
              <span className="sr-only">Links</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 bg-white dark:divide-zinc-800 dark:bg-zinc-950">
          {workflows.map((health) => (
            <WorkflowRow key={health.workflow} health={health} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One group, as its sections' tables under the group's heading, light and
 * reason.
 *
 * `showLabel` is false on a page whose own title already names the group — the
 * APHID Status page, where a heading reading "Platform Health" under a nav
 * section called Platform Health said it twice (TPF, 2026-09-09). The light and
 * the reason still render there: they are the group's state, not its name.
 */
export function GroupTable({
  group,
  showLabel = true,
}: {
  group: AddisWorkflowGroup;
  showLabel?: boolean;
}) {
  const { glyph, label } = LIGHT_PRESENTATION[group.light];
  return (
    <section className="space-y-2" data-testid={`addis-group-${group.key}`}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {showLabel ? (
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{group.label}</h3>
        ) : null}
        {/* The light, as the glyph alone — the group's reason is beside it in
            words, so naming the colour would say it twice. */}
        <span
          role="status"
          aria-label={`${label} — ${group.reason}`}
          title={`${label} — ${group.reason}`}
          className="inline-flex items-center text-sm leading-none"
        >
          <span aria-hidden="true">{glyph}</span>
        </span>
        <span className="text-xs text-zinc-500">{group.reason}</span>
      </div>
      <p className="text-xs text-zinc-500">{group.description}</p>

      <WorkflowSections workflows={group.workflows} />

      {group.unreadable.length > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Could not read: {group.unreadable.join(', ')}
        </p>
      )}
    </section>
  );
}
